import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ARPreviewModal } from '@/components/modals/ARPreviewModal';
import { getAnalysisById } from '@/services/showroom';
import type { ARGenerationResponse } from '@/services/showroom';
import { ImageWithFallback } from '@/components/ImageWithFallback';

export function AnalysisDetails() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [analysis, setAnalysis] = useState<ARGenerationResponse | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!analysisId) return;

      try {
        setIsLoading(true);
        const data = await getAnalysisById(analysisId);
        setAnalysis(data);
      } catch (error) {
        console.error('Error fetching analysis:', error);
        alert('Failed to load analysis details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-96" />
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-10 w-32 mt-4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="container mx-auto py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Analysis not found</h1>
        <p className="text-muted-foreground mb-4">
          The requested analysis could not be found or you don't have permission
          to view it.
        </p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Analysis Details</h1>
      </div>

      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-muted/10 rounded-lg overflow-hidden">
              <ImageWithFallback
                src={analysis.generatedImage}
                alt={`${analysis.product.name} in your room`}
                className="w-full h-auto max-h-[500px] object-contain"
                isStorageUrl={true}
              />
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-2">Product Information</h3>
                <h4 className="text-xl font-medium mb-2">
                  {analysis.product.name}
                </h4>
                <p className="text-muted-foreground">
                  {analysis.product.description}
                </p>
                <div className="mt-2 space-y-1 text-sm">
                  <p>
                    <span className="font-medium">Color:</span>{' '}
                    {analysis.product.color}
                  </p>
                  <p>
                    <span className="font-medium">Style:</span>{' '}
                    {analysis.room.roomStyle}
                  </p>
                  <p>
                    <span className="font-medium">Room Type:</span>{' '}
                    {analysis.room.roomType}
                  </p>
                  <p>
                    <span className="font-medium">Room Size:</span>{' '}
                    {analysis.room.roomArea.width_ft}ft ×{' '}
                    {analysis.room.roomArea.height_ft}ft
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium mb-2">Analysis Results</h3>
                <p className="text-muted-foreground">
                  This analysis was generated on{' '}
                  {new Date(analysis.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ARPreviewModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        arData={analysis}
        isLoading={false}
      />
    </div>
  );
}

export default AnalysisDetails;
